export function formatDateTime(date: string): string {
  return new Date(date).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatDate(date: string): string {
  return new Date(date).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function parseDuration(durationStr: string): number | null {
  if (!durationStr) return null;
  const match = durationStr.match(/(\d+)m (\d+)s/);
  if (match) {
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    return minutes * 60 + seconds;
  }
  // Try parsing just seconds if the format is different
  const secondsMatch = durationStr.match(/(\d+)s/);
  if (secondsMatch) {
      return parseInt(secondsMatch[1], 10);
  }
  // Try parsing just minutes
  const minutesMatch = durationStr.match(/(\d+)m/);
  if (minutesMatch) {
      return parseInt(minutesMatch[1], 10) * 60;
  }
  // Add more parsing logic if needed for other formats
  console.warn(`Could not parse duration: ${durationStr}`);
  return null;
} 
/* ───────────────────────────────────────────────────────────────────────────
   Formats d'affichage communs — plan de modernisation UX, §2 « Formats ».
   « 28 293 », « 83 % », « 78/100 », « 3 min 41 s », « −2,3 points ».
   `null` ou `undefined` donne « — » : une valeur absente n'est jamais un zéro.
   ─────────────────────────────────────────────────────────────────────────── */

/** Espace fine insécable, séparateur de milliers et d'unité en français. */
const NNBSP = ' ';
export const MISSING_VALUE = '—';

const isMissing = (value: number | null | undefined): value is null | undefined =>
  value === null || value === undefined || Number.isNaN(value);

/** Nombre entier groupé par milliers : « 28 293 ». */
export function formatCount(value: number | null | undefined): string {
  if (isMissing(value)) return MISSING_VALUE;
  return Math.round(value).toLocaleString('fr-FR').replace(/\s/g, NNBSP);
}

/** Part en pourcentage : « 83 % ». */
export function formatPercent(value: number | null | undefined, digits = 0): string {
  if (isMissing(value)) return MISSING_VALUE;
  return `${value.toLocaleString('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits })}${NNBSP}%`;
}

/** Note sur 100 : « 78/100 ». */
export function formatScore(value: number | null | undefined): string {
  if (isMissing(value)) return MISSING_VALUE;
  return `${Math.round(value)}/100`;
}

/** Durée lisible : « 3 min 41 s », « 45 s », « 1 h 02 min ». */
export function formatDurationLong(totalSeconds: number | null | undefined): string {
  if (isMissing(totalSeconds)) return MISSING_VALUE;
  const seconds = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}${NNBSP}h ${m.toString().padStart(2, '0')}${NNBSP}min`;
  if (m > 0) return `${m}${NNBSP}min ${s.toString().padStart(2, '0')}${NNBSP}s`;
  return `${s}${NNBSP}s`;
}

/** Écart en points de pourcentage, signe typographique : « +2 points », « −2,3 points ». */
export function formatPointsDelta(delta: number | null | undefined, digits = 1): string {
  if (isMissing(delta)) return MISSING_VALUE;
  const abs = Math.abs(delta).toLocaleString('fr-FR', { maximumFractionDigits: digits });
  if (abs === '0') return `0${NNBSP}point`;
  const sign = delta > 0 ? '+' : '−';
  return `${sign}${abs}${NNBSP}point${Math.abs(delta) >= 2 ? 's' : ''}`;
}

/** Durée renvoyée par la RPC (« 3m 41s ») convertie au format d'affichage. */
export function formatDurationString(value: string | null | undefined): string {
  if (!value) return MISSING_VALUE;
  const seconds = parseDuration(value);
  return seconds === null ? value : formatDurationLong(seconds);
}
